import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, Outlet} from "@tanstack/react-router"
import dayjs from "dayjs";
import React, {useEffect, useState} from "react";
import {apiAction} from "../api.ts";
import WelcomeTutorialModal from "../components/modals/WelcomeTutorialModal.tsx";
import PButton from "../components/PButton.tsx";
import {config} from "../config/config.ts";
import {UserStatus} from "../constants.ts";
import {useGlobalModal} from "../core/GlobalModal.tsx";
import {usePusher} from "../core/PusherContext.tsx";
import Header from "../Header.tsx";
import {usePullToRefresh} from "../hooks/usePullToRefresh.tsx";
import {useJwtAuth} from "../JWTAuthProvider.tsx";
import Private404Page from "../Private404Page.tsx";
import Paywall from "../user/Paywall.tsx";
import TrialIntroModal from "../user/TrialIntroModal.tsx";
import User_ProfileView_PlayerQuestionnaire from "../user/User_ProfileView_PlayerQuestionnaire.tsx";

const MAXIMUM_PULL_LENGTH = 240;
const REFRESH_THRESHOLD = 180;

export const Route = createFileRoute('/_app')({
    component: RouteComponent,

    notFoundComponent: () => (
        <Private404Page />
    ),
})

function RouteComponent() {
    const jwt = useJwtAuth();
    const user = jwt.user;
    const pusher = usePusher();
    const queryClient = useQueryClient();
    const globalModal = useGlobalModal();
    const [lastReloadTs, setLastReloadTs] = useState(0);

    const versionQuery = useQuery({
        queryKey: ['version'],
        queryFn:  async () => fetch(config.apiUrl + '/version', {method: 'post'}).then((r) => r.text()),
    })

    const handler = (event) => {
        if (event.table === 'messages') {
            jwt.reloadUser();
            if (!document.location.href.includes('/messages')) {
                addToast({
                    title:       'New message from ' + event.data._sender_name,
                    description: event.data.content?.substring(0, 100),
                });
            }
        }
    }

    useEffect(() => {
        if (!jwt.initialized) {
            return;
        }

        pusher.subscribeTo('channel_user_' + (user?.uid || 'anonymous'));
        pusher.on(handler);

        return () => {
            pusher.off(handler);
            pusher.unsubscribeFrom('channel_user_' + (user?.uid || 'anonymous'));
        }
    }, [user]);

    useEffect(() => {
        if (!user) {
            return;
        }

        if (!user.is_subscribed && (user?.trial_seconds_left || 0) > 0 && !user?.meta?.trial_intro_seen) {
            globalModal.open(<TrialIntroModal />);
        } else if (!user.is_subscribed && (user?.trial_seconds_left || 0) < 0 && !sessionStorage.trial_ended_seen) {
            globalModal.open(<Paywall trialHasExpired={true} onClose={() => {
                sessionStorage.trial_ended_seen = 'y';
                apiAction('users-update', {
                    uid:    user!.uid,
                    values: {
                        version:                    user!.version + 1,
                        ['meta->trial_ended_seen']: true,
                    },
                });
            }} />);
        } else if (user?.status && +user?.status !== UserStatus.Incomplete && !user?.meta?.welcome_tutorial_seen) {
            globalModal.open(<WelcomeTutorialModal onClose={() => {
                apiAction('users-update', {
                    uid:    user!.uid,
                    values: {
                        version:                         user!.version + 1,
                        ['meta->welcome_tutorial_seen']: true,
                    },
                });
            }} />);
        } else if (user?.status && +user?.status !== UserStatus.Incomplete && !user?.meta?.welcome_tutorial_seen) {
            globalModal.open(<WelcomeTutorialModal onClose={() => {
                apiAction('users-update', {
                    uid:    user!.uid,
                    values: {
                        version:                         user!.version + 1,
                        ['meta->welcome_tutorial_seen']: true,
                    },
                });
            }} />);
        }
    }, [user]);

    useEffect(() => {
        if (versionQuery.data && versionQuery.data !== config.version && config.appEnv !== 'local' && !config.isCordova) {
            addToast({
                timeout:    0,
                title:      `New version is available: ${versionQuery.data}`,
                color:      "warning",
                endContent: <PButton color={"primary"} onPress={() => document.location.reload()}>Update</PButton>,
            });
        }
    }, [versionQuery.data]);

    const {isRefreshing, pullPosition} = usePullToRefresh({
        // you can choose what behavior for `onRefresh`, could be calling an API to load more data, or refresh whole page.
        onRefresh:         () => {
            queryClient.clear()
            if (dayjs().unix() - lastReloadTs <= 2) {
                document.location.reload();
            }
            setLastReloadTs(dayjs().unix())
        },
        maximumPullLength: MAXIMUM_PULL_LENGTH,
        refreshThreshold:  REFRESH_THRESHOLD,
        isDisabled:        !config.isCordova,
    });

    if (!jwt.initialized) {
        return <>
            <Header showBackUrl={false} />

            <div className="text-center">
                <Spinner className="mx-auto"></Spinner>
            </div>
        </>
    }

    if (+(user?.status || 0) === UserStatus.Incomplete) {
        return <>
            <Header showBackUrl={false} />

            <User_ProfileView_PlayerQuestionnaire />
        </>
    }

    return (
        <>
            <div
                style={{
                    top:     (isRefreshing ? REFRESH_THRESHOLD : pullPosition) / 3,
                    opacity: isRefreshing || pullPosition > 0 ? 1 : 0,
                }}
                className={'bg-default-100 fixed inset-x-1/2 z-30 h-12 w-12 -translate-x-1/2 rounded-full p-2 shadow'}
            >
                <div
                    className={`h-full w-full ${isRefreshing ? `animate-spin` : ``}`}
                    style={!isRefreshing ? {transform: `rotate(${pullPosition}deg)`} : {}}
                >
                    <Icon icon={"material-symbols-light:refresh"} className={"h-full w-full"} />
                </div>
            </div>

            <Outlet />
        </>
    );
}
